import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    b2cAccountUrl: string;
    isLink?: boolean;
    extendedStyles?: StoreCreateAccountModalStyles;
}

export interface StoreCreateAccountModalStyles {
    createAccountLink?: LinkPresentationProps;
    createAccountButton?: ButtonPresentationProps;
    createAccountModal?: ModalPresentationProps;
    container?: GridContainerProps;
    columnStyle?: GridItemProps;
    separator?: InjectableCss;
    separatorWrapper?: InjectableCss;
    verticalSeparator?: InjectableCss;
    separatorText?: TypographyProps;
    titleWrapper?: InjectableCss;
    sectionTitle?: TypographyProps;
    coloredSectionTitle?: TypographyProps;
    accountTypeWrapper?: InjectableCss;
    description?: TypographyProps;
}

export const storeCreateAccountModalStyles: StoreCreateAccountModalStyles = {
    createAccountLink: {
        typographyProps: {
            css: css`
                text-decoration: underline;

                &:hover,
                &:focus {
                    text-decoration: none;
                }
            `,
        },
    },
    createAccountButton: {
        variant: "secondary",
    },
    createAccountModal: {
        size: 1300,
    },
    container: {
        gap: 0,
        css: css`
            padding: 20px 0 0;

            ${GridOffset} {
                justify-content: space-between;
            }
        `,
    },
    columnStyle: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-self: flex-start;
            flex-wrap: wrap;
            padding: 0;
        `,
    },
    separator: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["100%", null, null, "auto", null], properties: ["width"] },
                        { values: ["row", null, null, "column", null], properties: ["flex-direction"] },
                        { values: ["30px 0", null, null, "0 20px", null], properties: ["margin"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    verticalSeparator: {
        css: css`
            flex-grow: 1;
            background-color: ${COLORS.border.lighter};

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["100%", null, null, "2px", null], properties: ["width"] },
                        { values: ["2px", null, null, "100%", null], properties: ["height"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    separatorText: {
        color: COLORS.text.lighter,
        size: TYPOGRAPHY.size.base,
        transform: "uppercase",
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            padding: 20px;
        `,
    },
    titleWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
        `,
    },
    sectionTitle: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
            margin-right: 5px;
            white-space: nowrap;
        `,
    },
    coloredSectionTitle: {
        variant: "h2",
        color: COLORS.primary.base,
        css: css`
            margin-top: 0;
            white-space: nowrap;
        `,
    },
    accountTypeWrapper: {
        css: css`
            width: 100%;
            display: block;
            margin: 20px 0;
        `,
    },
    description: {
        variant: "p",
        color: COLORS.text.base,
    },
};

const CreateAccountModal = ({ b2cAccountUrl, isLink, extendedStyles }: OwnProps) => {
    const history = useHistory();
    const [styles] = React.useState(() => mergeToNew(storeCreateAccountModalStyles, extendedStyles));
    const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] = useState(false);

    const b2bExistingAccountTextMessage = translate("CreateAccountModal_existingB2b_text");
    const b2bExistingAccountUrlMessage = translate("CreateAccountModal_existingB2b_url");
    const b2bExistingAccountBtnMessage = translate("CreateAccountModal_existingB2b_btn");
    const b2bNewAccountTextMessage = translate("CreateAccountModal_newB2b_text");
    const b2bNewAccountUrlMessage = translate("CreateAccountModal_newB2b_url");
    const b2bNewAccountBtnMessage = translate("CreateAccountModal_newB2b_btn");
    const b2cAccountTextMessage = translate("CreateAccountModal_newB2c_text");
    const b2cAccountBtnMessage = translate("CreateAccountModal_newB2c_btn");

    const onCreateAccountModalClose = () => {
        setIsCreateAccountModalOpen(false);
    };

    const renderCreateAccountType = (text: string, url: string, cta: string) => {
        if (!text && !url && !cta) {
            return;
        }

        const onClickCta = () => {
            if (url.startsWith("http")) {
                window.open(url, "_blank");
            } else {
                history.push(url);
            }
        };

        return (
            <StyledWrapper {...styles.accountTypeWrapper}>
                <Typography {...styles.description}>{text}</Typography>
                <Button onClick={onClickCta} {...styles.createAccountButton}>
                    {cta}
                </Button>
            </StyledWrapper>
        );
    };

    const renderModalContent = () => {
        return (
            <GridContainer {...styles.container}>
                <GridItem {...styles.columnStyle}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.sectionTitle}>{translate("Create a New")}</Typography>
                        <Typography {...styles.coloredSectionTitle}>{translate("Business Account")}</Typography>
                    </StyledWrapper>
                    {renderCreateAccountType(
                        b2bExistingAccountTextMessage,
                        b2bExistingAccountUrlMessage,
                        b2bExistingAccountBtnMessage,
                    )}
                    {renderCreateAccountType(
                        b2bNewAccountTextMessage,
                        b2bNewAccountUrlMessage,
                        b2bNewAccountBtnMessage,
                    )}
                </GridItem>
                <StyledWrapper {...styles.separator}>
                    <StyledWrapper {...styles.verticalSeparator}></StyledWrapper>
                    <Typography {...styles.separatorText}>{translate("or")}</Typography>
                    <StyledWrapper {...styles.verticalSeparator}></StyledWrapper>
                </StyledWrapper>
                <GridItem {...styles.columnStyle}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.sectionTitle}>{translate("Create a New")}</Typography>
                        <Typography {...styles.coloredSectionTitle}>{translate("Personnal Account")}</Typography>
                    </StyledWrapper>
                    {renderCreateAccountType(b2cAccountTextMessage, b2cAccountUrl, b2cAccountBtnMessage)}
                </GridItem>
            </GridContainer>
        );
    };

    return (
        <>
            {isLink ? (
                <Link {...styles.createAccountLink} onClick={() => setIsCreateAccountModalOpen(true)}>
                    {translate("Create Account")}
                </Link>
            ) : (
                <Button {...styles.createAccountButton} onClick={() => setIsCreateAccountModalOpen(true)}>
                    {translate("Create Account")}
                </Button>
            )}
            <Modal
                headline={translate("Create Account")}
                {...styles.createAccountModal}
                isOpen={isCreateAccountModalOpen}
                handleClose={() => onCreateAccountModalClose()}
            >
                {renderModalContent()}
            </Modal>
        </>
    );
};

export default CreateAccountModal;
